import axios from "axios";
import Cookies from "js-cookie";
import { slideToggle, slideUp } from "@roghz/slidetoggle";
import popupConfirm from "../../assets/scripts/common/popupConfirm";

document.addEventListener("DOMContentLoaded", () => {
  const productsListBlock = document.querySelector(".products-list");
  if (!productsListBlock) {
    return;
  }

  const elements = {
    form: productsListBlock.querySelector(".products-list__filters--form"),
    wrapper: productsListBlock.querySelector(".products-list__filters--wrapper"),
    grid: productsListBlock.querySelector(".products-list__grid--grid"),
    pagination: productsListBlock.querySelector(".pagination"),
    sorting: productsListBlock.querySelector(".products-list__grid--sorting"),
    resultsCount: productsListBlock.querySelector(".products-list__grid--count span"),
    toggleBtn: productsListBlock.querySelector(".toggle-filters"),
    filterCount: productsListBlock.querySelector(".filter-count"),
    clearBtn: null
  };

  // Only require grid and sorting elements - form is optional (hidden filters case)
  if (!elements.grid || !elements.sorting) return;

  // Only set clearBtn if form exists
  if (elements.form) {
    elements.clearBtn = elements.form.querySelector(".clear-filters");
  }

  // SECURITY: Whitelist of allowed post types
  const ALLOWED_POST_TYPES = [
    'td_video',
    'product',
    'post',
    'podcast',
    'td_manual',
    'favorite-finds',
    'faqs'
  ];

  // Allowed sorting options
  const ALLOWED_SORT_OPTIONS = [
    '',
    'date',
    'price',
    'price-desc',
    'popularity',
    'rating',
    'alphabetical',
    'title'
  ];

  const state = {
    restUrl: window.productsListData?.restUrl || '',
    categoryId: window.productsListData?.categoryId,
    postType: window.productsListData?.postType,
    loading: false,
    prefetchedUrls: new Set()
  };

  if (!state.restUrl) return;

  // Clean the REST URL once at initialization
  state.restUrl = state.restUrl.split('?')[0]; // Remove query string
  state.restUrl = state.restUrl.replace(/\/page\/\d+\/?/g, ''); // Remove /page/X/ segments
  state.restUrl = state.restUrl.replace(/\/+$/, ''); // Remove trailing slashes

  // SECURITY: Validate post type
  if (!ALLOWED_POST_TYPES.includes(state.postType)) {
    return;
  }

  if (state.postType === 'product' && !state.categoryId) return;

  const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

  const toggleMobileFilters = () => {
    if (elements.wrapper && elements.toggleBtn) {
      elements.toggleBtn.addEventListener("click", (e) => {
        e.preventDefault();
        slideToggle(elements.wrapper);
      });
    }
  };

  const getOriginParam = () => {
    const originCookie = Cookies.get('origin');
    return originCookie === 'international' ? 'i' : null;
  };

  const getFormData = () => {
    const params = new URLSearchParams();

    // Only process form data if form exists (filters not hidden)
    if (elements.form) {
      const formData = new FormData(elements.form);
      for (const [key, value] of formData.entries()) {
        if (value && value.trim() !== '') {
          // Keep array notation for proper server-side parsing
          params.append(key, value);
        }
      }

      // If min_price/max_price are not in form but are in URL, use URL values
      const urlParams = new URLSearchParams(window.location.search);
      if (!params.has('min_price') && urlParams.has('min_price')) {
        const urlMinPrice = urlParams.get('min_price');
        if (urlMinPrice && urlMinPrice.trim() !== '') {
          params.set('min_price', urlMinPrice);
        }
      }
      if (!params.has('max_price') && urlParams.has('max_price')) {
        const urlMaxPrice = urlParams.get('max_price');
        if (urlMaxPrice && urlMaxPrice.trim() !== '') {
          params.set('max_price', urlMaxPrice);
        }
      }
    }

    if (elements.sorting.value && elements.sorting.value !== '') {
      // SECURITY: Validate sorting option
      if (ALLOWED_SORT_OPTIONS.includes(elements.sorting.value)) {
        params.set('orderby', elements.sorting.value);
      }
    }

    // Add ID parameter - for products use categoryId, for others use a default or skip
    if (state.postType === 'product' && state.categoryId) {
      params.set('id', state.categoryId);
    } else if (state.postType !== 'product') {
      // For non-products, use a default ID of 0 or skip the ID requirement
      params.set('id', '0');
    }

    // Add origin parameter for international users (for different cache)
    const origin = getOriginParam();
    if (origin) {
      params.set('o', origin);
    }

    // Add list widget ID if available
    if (window.productsListData?.listWidgetId) {
      params.set('list_widget', window.productsListData.listWidgetId);
    }

    // Add posts_per_page if available
    if (window.productsListData?.postsPerPage) {
      params.set('posts_per_page', window.productsListData.postsPerPage);
    }

    // Add post_type if available
    if (state.postType) {
      params.set('post_type', state.postType);
    }

    // Add card template if available
    if (window.productsListData?.cardTemplate) {
      params.set('card_template', window.productsListData.cardTemplate);
    }

    // Add taxonomies for filtering
    if (window.productsListData?.taxonomies) {
      params.set('taxonomies', JSON.stringify(window.productsListData.taxonomies));
    }

    return params;
  };


  const updateURL = (params, pageNum = 1) => {
    // For native WordPress pagination, handle URL updates properly
    const url = new URL(window.location.href);

    // Use provided page number or default to 1
    const isPageOne = pageNum === 1;

     // Remove /page/X/ from URL path first
     url.pathname = url.pathname.replace(/\/page\/\d+\/?/, '');

     // Add /page/X/ for pages > 1 only
     if (!isPageOne) {
       // Ensure pathname ends with / before adding page
       if (!url.pathname.endsWith('/')) {
         url.pathname += '/';
       }
       url.pathname += `page/${pageNum}/`;
     }
     // For page 1, ensure pathname ends with / but don't add /page/1/
     else if (url.pathname !== '/' && !url.pathname.endsWith('/')) {
       url.pathname += '/';
     }

     // Remove pagination and internal API parameters from browser URL
     // These are only needed for AJAX requests, not for the visible URL
     ['paged', 'id', 'posts_per_page', 'post_type', 'list_widget', 'card_template', 'taxonomies'].forEach(key => params.delete(key));

     // Always respect cookie origin over URL parameter
     const cookieOrigin = getOriginParam();
     if (cookieOrigin) {
       params.set('o', cookieOrigin);
     } else {
       params.delete('o'); // Remove if cookie is domestic
     }

     const defaults = { orderby: '', sale: '', o: '' };

     for (const [key, value] of params.entries()) {
       const defaultValue = defaults[key];
       const shouldRemove = defaultValue === value;

       if (shouldRemove || value === '') {
         params.delete(key);
       }
     }

     url.search = params.toString();
     window.history.pushState({}, '', url);
   };

  const updateUI = (data) => {
    if (elements.grid) {
      elements.grid.innerHTML = data.products_html || '';

      if (data.widget_html && data.products_html) {
        const widgetSlot = elements.grid.querySelector('.widget-slot[data-widget-position="6"]');
        if (widgetSlot) widgetSlot.innerHTML = data.widget_html;
      }
    }

    if (elements.pagination) {
      elements.pagination.innerHTML = data.pagination || '';
      // No need to rebind - using event delegation on parent
    }

    if (elements.resultsCount && typeof data.total_results !== 'undefined') {
      elements.resultsCount.textContent = data.total_results;
    }

    // Handle conditional display of "Special Offers" filter based on AJAX response
    if (typeof data.has_sale_products !== 'undefined') {
      const saleFilterGroup = document.querySelector('.products-list__filters--group.sale');
      if (saleFilterGroup) {
        if (data.has_sale_products) {
          saleFilterGroup.style.display = 'block';
        } else {
          saleFilterGroup.style.display = 'none';
        }
      }
    }

    updateFilterCount();

    if (window.reInitializePrefetch) {
      window.reInitializePrefetch();
    }
  };

  const loadProducts = async (additionalParams = {}, retryCount = 0) => {
    await loadProductsInternal(additionalParams, retryCount, true);
  };

  const loadProductsWithoutURLUpdate = async (additionalParams = {}, retryCount = 0) => {
    await loadProductsInternal(additionalParams, retryCount, false);
  };

  const loadProductsInternal = async (additionalParams = {}, retryCount = 0, updateUrlAfter = true) => {
    if (state.loading) return;

    state.loading = true;
    productsListBlock.classList.add("loading");

    try {
      const params = getFormData();

       Object.entries(additionalParams).forEach(([key, value]) => {
         if (value) {
           params.set(key, value);
         }
       });

      // Build the API endpoint - state.restUrl should already be clean
      const apiEndpoint = `${state.restUrl}/products`;

      const response = await axios.get(apiEndpoint, {
        params,
        timeout: 30000
      });

      if (response.data) {
        updateUI(response.data);

        if (updateUrlAfter) {
          // Always update URL for filter changes and pagination
          // Create a clean copy of params for URL updating (remove internal parameters)
          const cleanParams = new URLSearchParams(params);
          const internalParams = ['id', 'posts_per_page', 'post_type', 'card_template', 'taxonomies', 'list_widget'];
          internalParams.forEach(key => cleanParams.delete(key));

          // For filter changes, always reset to page 1
          // For pagination, use the page number from additionalParams
          const pageNum = additionalParams.paged !== undefined ? additionalParams.paged : 1;
          cleanParams.delete('paged'); // Remove paged from clean params
          updateURL(cleanParams, pageNum);
        }

         // Scroll to block on page changes (but not on initial filter changes on desktop)
         if (additionalParams.paged !== undefined && additionalParams.paged !== 1) {
           scrollToBlock();
         } else {
           // For filter changes, scroll only if block is not visible
           scrollToBlockIfNotVisible();
         }
      }
    } catch (error) {
      // Show error message to user

      let errorTitle = 'Error Loading Products';
      let errorMessage = 'Failed to load products. Please try again.';

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorTitle = 'Request Timed Out';
        errorMessage = 'The server is taking too long to respond. This might be due to high traffic or server issues.';
      } else if (error.response) {
        errorTitle = 'Server Error';
        errorMessage = `The server returned an error: ${error.response.status} ${error.response.statusText}`;
      } else if (error.request) {
        errorTitle = 'Connection Error';
        errorMessage = 'No response from server. Please check your internet connection.';
      }

      // Show popup confirmation
      const shouldReload = await popupConfirm(
        errorTitle,
        errorMessage,
        'Would you like to reload the page?',
        'Reload Page',
        'Cancel'
      );

      if (shouldReload) {
        window.location.reload();
      } else {
        // Reset checkboxes to previous state if user cancels
        loadProductsFromURL();
      }
    } finally {
      state.loading = false;
      productsListBlock.classList.remove("loading");
    }
  };

  const scrollToBlock = () => {
    const rect = productsListBlock.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const offsetTop = rect.top + scrollTop - 100; // 100px offset from top
    window.scrollTo({ top: offsetTop, behavior: "smooth" });
  };

  const scrollToBlockIfNotVisible = () => {
    const rect = productsListBlock.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;

    if (!isVisible) {
      scrollToBlock();
    }
  };

  const updateFilterCount = () => {
    let totalSelected = 0;

    // Only count filters if form exists (filters not hidden)
    if (elements.form) {
      const checkedInputs = elements.form.querySelectorAll('input[type="checkbox"]:checked');
      const filledInputs = elements.form.querySelectorAll('input[type="number"]');
      const filledCount = Array.from(filledInputs).filter(input => input.value.trim() !== "").length;
      totalSelected = checkedInputs.length + filledCount;
    }

    if (elements.filterCount) {
      if (totalSelected > 0) {
        elements.filterCount.textContent = totalSelected;
        elements.filterCount.style.display = 'inline';
      } else {
        elements.filterCount.style.display = 'none';
      }
    }

    if (elements.clearBtn) {
      if (totalSelected > 0) {
        elements.clearBtn.style.display = '';
      } else {
        elements.clearBtn.style.display = 'none';
      }
    }
  };

  const loadProductsFromURL = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    const paramValues = {};

    for (const [key, value] of urlParams.entries()) {
      if (!paramValues[key]) paramValues[key] = [];
      paramValues[key].push(value);
      params[key] = value;
    }

    // Always override URL origin parameter with cookie value
    const cookieOrigin = getOriginParam();
    if (cookieOrigin) {
      params.o = cookieOrigin;
      paramValues.o = [cookieOrigin];
    } else {
      delete params.o;
      delete paramValues.o;
    }

    // Only reset form if it exists (filters not hidden)
    if (elements.form) {
      elements.form.reset();

      for (const [key, values] of Object.entries(paramValues)) {
        // Skip origin parameter as it's cookie-controlled
        if (key === 'o') continue;

        if (Array.isArray(values)) {
          values.forEach(val => {
            // Strip trailing slashes to match term slugs
            const cleanVal = val.replace(/\/$/, '');
            const input = elements.form.querySelector(`input[name="${key}[]"][value="${cleanVal}"]`);
            if (input?.type === 'checkbox') input.checked = true;
          });
        }

        const singleInput = elements.form.querySelector(`input[name="${key}"], select[name="${key}"]`);
        if (singleInput && !singleInput.name.includes('[]')) {
          if (singleInput.type === 'checkbox') {
            singleInput.checked = values.includes('on') || values.includes('1');
          } else {
            // Handle number, text, and select inputs
            const valueToSet = Array.isArray(values) ? values[values.length - 1] : values;
            if (valueToSet !== undefined && valueToSet !== null && valueToSet !== '') {
              singleInput.value = valueToSet;
            }
          }
        }
      }
    }

    if (params.orderby && params.orderby !== '') {
      elements.sorting.value = params.orderby;
    } else {
      elements.sorting.value = '';
    }

    updateFilterCount();
    await loadProducts(params);
  };

  const handlePaginationClick = async (e) => {
    // Check if click is on a pagination link or button
    const linkTarget = e.target.closest(".pagination a.page-numbers, .pagination a.next, .pagination a.prev");
    const buttonTarget = e.target.closest(".pagination button[data-page]");

    const target = linkTarget || buttonTarget;

    if (target) {
      e.preventDefault();

      let pageNum = 1;

      // Handle button with data-page attribute
      if (target.dataset && target.dataset.page) {
        pageNum = parseInt(target.dataset.page);
      }
      // Handle link with href
      else if (target.href) {
        const url = new URL(target.href);
        const pageMatch = url.pathname.match(/\/page\/(\d+)/);
        if (pageMatch) {
          pageNum = parseInt(pageMatch[1]);
        }
      }

      // Load products with AJAX
      await loadProducts({ paged: pageNum });

      // Scroll to the block
      scrollToBlock();
    }
  };

  const initProductPrefetch = () => {
    const prefetchUrl = (url) => {
      if (state.prefetchedUrls.has(url)) return;
      state.prefetchedUrls.add(url);

      const linkEl = document.createElement('link');
      linkEl.rel = 'prefetch';
      linkEl.href = url;
      document.head.appendChild(linkEl);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const productCard = entry.target;
          const productLink = productCard.querySelector('a[href]:not([href*="add-to-cart"]):not([href*="cart"]):not([href*="#"])') ||
                            productCard.querySelector('a[href*="/product"], a[href*="/shop"]');

          if (productLink?.href) {
            setTimeout(() => prefetchUrl(productLink.href), 100);
            observer.unobserve(productCard);
          }
        }
      });
    }, { threshold: 0.2, rootMargin: '200px' });

    const observeProducts = () => {
      const productCards = elements.grid.querySelectorAll('.product-list-item, .product-container-card, [class*="product"]');
      productCards.forEach(card => {
        if (card.querySelector('a[href]')) observer.observe(card);
      });
    };

    observeProducts();
    return observeProducts;
  };

  const bindEvents = () => {
    toggleMobileFilters();

    // Only bind form events if form exists (filters not hidden)
    if (elements.form) {
      elements.form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.addEventListener("change", async () => {
            const parentLabel = checkbox.closest('.products-list__filters--option');
            const childrenBlock = parentLabel?.nextElementSibling;

            if (
                childrenBlock &&
                childrenBlock.classList.contains('products-list__filters--option-children') &&
                !checkbox.checked
            ) {
                childrenBlock.querySelectorAll('input[type="checkbox"]').forEach(child => {
                    child.checked = false;
                });
            }
          if (isMobile() && elements.wrapper) slideUp(elements.wrapper);
          // Reset to page 1 when filters change
          await loadProducts({ paged: 1 });
        });
      });

      // Search input handler with debouncing
      const searchInput = elements.form.querySelector('input[name="search"]');
      if (searchInput) {
        let searchTimeout;
        searchInput.addEventListener("input", () => {
          clearTimeout(searchTimeout);
          searchTimeout = setTimeout(async () => {
            // Reset to page 1 when search changes
            await loadProducts({ paged: 1 });
          }, 500); // 500ms debounce
        });
      }

      elements.form.querySelectorAll('.number-wrap').forEach(wrap => {
        const input = wrap.querySelector('input[type="number"]');
        const clearButton = wrap.querySelector('.clear');

        if (input && clearButton) {
          const toggleClearButton = () => {
            clearButton.style.display = input.value.trim() !== "" ? "flex" : "none";
          };

          toggleClearButton();
          input.addEventListener("input", toggleClearButton);
           clearButton.addEventListener("click", (e) => {
             e.preventDefault();
             input.value = "";
             toggleClearButton();
             updateFilterCount();
             // Reset to page 1 when filters change
             loadProducts({ paged: 1 });
           });
        }
      });

      // Price filter inputs with auto-apply after 2 seconds
      const minPriceInput = elements.form.querySelector('input[name="min_price"]');
      const maxPriceInput = elements.form.querySelector('input[name="max_price"]');
      let priceTimeout;

      const handlePriceChange = () => {
        clearTimeout(priceTimeout);
        priceTimeout = setTimeout(async () => {
          // Reset to page 1 when price filters change
          await loadProducts({ paged: 1 });
        }, 2000); // 2 second debounce
      };

      if (minPriceInput) {
        minPriceInput.addEventListener("input", handlePriceChange);
      }
      if (maxPriceInput) {
        maxPriceInput.addEventListener("input", handlePriceChange);
      }

      // Price filter submit button
      const priceSubmitBtn = elements.form.querySelector('.price-filter-submit');
      if (priceSubmitBtn) {
        priceSubmitBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          clearTimeout(priceTimeout); // Clear any pending auto-apply
          await loadProducts({ paged: 1 });
        });
      }

      if (elements.clearBtn) {
        elements.clearBtn.addEventListener('click', async (e) => {
          e.preventDefault();

          // Reset form and sorting
          elements.form.reset();
          elements.sorting.value = '';

          // Clear URL first
          const url = new URL(window.location.href);
          url.search = '';
          window.history.pushState({}, '', url);

          // Update UI
          updateFilterCount();

          // Load products without updating URL again
          await loadProductsWithoutURLUpdate();
        });
      }

      elements.form.addEventListener("change", updateFilterCount);
    }

     elements.sorting.addEventListener("change", (e) => {
       // Only trigger if this is a user-initiated change, not programmatic
       if (e.isTrusted) {
         // Reset to page 1 when sorting changes
         loadProducts({ paged: 1 });
       }
     });

    // Use event delegation for pagination - listen on parent block
    productsListBlock.addEventListener("click", handlePaginationClick);

    window.addEventListener("popstate", loadProductsFromURL);
  };

  const hasFilterParamsInURL = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // Parameters that should NOT trigger scroll (internal/navigation params)
    const nonFilterParams = ['o', 'paged', 'id', 'posts_per_page', 'post_type', 'list_widget', 'card_template', 'taxonomies'];

    // Check all URL parameters
    for (const [key] of urlParams.entries()) {
      // Skip non-filter parameters
      if (nonFilterParams.includes(key)) {
        continue;
      }

      // Any other parameter is considered a filter
      // This includes:
      // - WooCommerce attributes with array notation: skill-level[], color[], etc.
      // - Standard filters: c[], sale, orderby, search
      // - Custom taxonomy filters for other post types
      return true;
    }

    return false;
  };

  const init = () => {
    updateFilterCount();
    bindEvents();

    // Scroll to block if there are filter parameters in the URL on initial load
    if (hasFilterParamsInURL()) {
      setTimeout(() => {
        scrollToBlock();
      }, 300); // Small delay to ensure page is fully rendered
    }

    let observeProducts;
    setTimeout(() => {
      observeProducts = initProductPrefetch();
      window.reInitializePrefetch = () => {
        if (observeProducts && typeof observeProducts === 'function') {
          setTimeout(() => observeProducts(), 100);
        }
      };
    }, 500);
  };

  init();
});
